import React, { useMemo, useRef, useState } from "react";
import { Plus, Save, Trash2 } from "lucide-react";
import ConfirmationModal from "./ConfirmationModal.jsx";
import { previewCatalogProductRename } from "../lib/dataManagerApi.js";
import { buildRenameImpactItems, isProductInUse } from "../utils/productRenameImpact.js";

const MAX_PRODUCT_NAME_LENGTH = 120;
const PRODUCT_NAME_COLLATOR = new Intl.Collator("fr-CA", { numeric: true, sensitivity: "base" });
const PANEL_CLASS_NAME = "mx-auto w-full max-w-5xl min-w-0 rounded-[14px] border border-slate-200 bg-white p-3";
const PRIMARY_ACTION_CLASS_NAME = "inline-flex h-8 items-center gap-1.5 rounded-full bg-accent px-3 text-[11px] font-semibold text-white transition-colors duration-150 hover:bg-accent-strong disabled:cursor-not-allowed disabled:opacity-60";
const SECONDARY_ACTION_CLASS_NAME = "inline-flex h-8 items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 text-[11px] font-semibold text-slate-700 transition-colors duration-150 hover:border-slate-300 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-60";
const DANGER_ACTION_CLASS_NAME = "inline-flex h-8 items-center gap-1.5 rounded-full border border-rose-200 bg-white px-3 text-[11px] font-semibold text-rose-700 transition-colors duration-150 hover:border-rose-300 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60";
const INPUT_CLASS_NAME = "h-8 w-full min-w-0 rounded-[8px] border border-slate-200 bg-white px-2.5 text-[12px] text-slate-800 outline-hidden transition-colors duration-150 placeholder:text-slate-400 focus:border-accent focus:ring-2 focus:ring-accent/15 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500";
const HEADER_CELL_CLASS_NAME = "border-b border-slate-200 bg-slate-50/95 px-2.5 py-2 text-left text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-700";
const CELL_CLASS_NAME = "border-b border-slate-100 px-2.5 py-2 align-middle text-[11px] text-slate-700";

function normalizeText(value) {
  return value === null || value === undefined ? "" : String(value);
}

function productName(product) {
  return normalizeText(product?.name);
}

function updateKey(productId) {
  return `product-catalog:update:${productId}`;
}

function deleteKey(productId) {
  return `product-catalog:delete:${productId}`;
}

export default function DataManagerProductCatalogTab({
  products = [],
  searchQuery = "",
  canWrite = false,
  onCreate,
  onUpdate,
  onDelete,
  isPending = () => false,
}) {
  const [isCreating, setIsCreating] = useState(false);
  const [createName, setCreateName] = useState("");
  const [editingProductId, setEditingProductId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingUpdatedAt, setEditingUpdatedAt] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [renameCandidate, setRenameCandidate] = useState(null);
  const [localPendingKey, setLocalPendingKey] = useState(null);
  const localPendingKeyRef = useRef(null);

  const visibleProducts = useMemo(() => {
    const query = normalizeText(searchQuery).trim().toLocaleLowerCase("fr-CA");
    return (Array.isArray(products) ? products : [])
      .filter((product) => productName(product).toLocaleLowerCase("fr-CA").includes(query))
      .slice()
      .sort((left, right) => (
        PRODUCT_NAME_COLLATOR.compare(productName(left), productName(right))
        || PRODUCT_NAME_COLLATOR.compare(normalizeText(left?.product_id), normalizeText(right?.product_id))
      ));
  }, [products, searchQuery]);

  function externalPending(key) {
    return typeof isPending === "function" && Boolean(isPending(key));
  }

  function actionPending(key) {
    return localPendingKey === key || externalPending(key);
  }

  function startPending(key) {
    if (localPendingKeyRef.current || externalPending(key)) return false;
    localPendingKeyRef.current = key;
    setLocalPendingKey(key);
    return true;
  }

  function finishPending(key) {
    if (localPendingKeyRef.current !== key) return;
    localPendingKeyRef.current = null;
    setLocalPendingKey(null);
  }

  function cancelCreate() {
    setCreateName("");
    setIsCreating(false);
  }

  function cancelEdit() {
    setEditingProductId(null);
    setEditingName("");
    setEditingUpdatedAt(null);
  }

  function startEdit(product) {
    const productId = product?.product_id;
    if (!canWrite || localPendingKeyRef.current || productId === null || productId === undefined || productId === "" || actionPending(updateKey(productId))) return;
    setIsCreating(false);
    setCreateName("");
    setEditingProductId(productId);
    setEditingName(productName(product));
    setEditingUpdatedAt(product?.updated_at);
  }

  async function submitCreate(event) {
    event?.preventDefault?.();
    const key = "product-catalog:create";
    const name = createName.trim();
    if (!canWrite || !name || name.length > MAX_PRODUCT_NAME_LENGTH || !startPending(key)) return;
    try {
      if (await onCreate?.({ name })) cancelCreate();
    } catch { /* Keep the draft after a rejected callback. */ } finally {
      finishPending(key);
    }
  }

  async function submitEdit(event, product) {
    event?.preventDefault?.();
    const productId = product?.product_id;
    const key = updateKey(productId);
    const name = editingName.trim();
    if (!canWrite || editingProductId !== productId || !name || name.length > MAX_PRODUCT_NAME_LENGTH || !startPending(key)) return;
    try {
      // Un produit utilisé est renommé partout : la fenêtre compte d'abord ce qui change.
      const preview = name === productName(product) ? null : await previewCatalogProductRename(productId).catch(() => null);
      if (isProductInUse(preview)) {
        setRenameCandidate({ product, name, preview });
        return;
      }
      if (await onUpdate?.(productId, { name, expected_updated_at: editingUpdatedAt })) cancelEdit();
    } catch { /* Keep the draft after a rejected callback. */ } finally {
      finishPending(key);
    }
  }

  async function confirmRename() {
    const productId = renameCandidate?.product?.product_id;
    const key = updateKey(productId);
    if (!canWrite || !renameCandidate || !startPending(key)) return;
    try {
      if (await onUpdate?.(productId, { name: renameCandidate.name, expected_updated_at: editingUpdatedAt })) {
        setRenameCandidate(null);
        cancelEdit();
      }
    } catch { /* Keep the confirmation open after a rejected callback. */ } finally {
      finishPending(key);
    }
  }

  async function toggleActive(product) {
    const productId = product?.product_id;
    const key = updateKey(productId);
    if (!canWrite || productId === null || productId === undefined || productId === "" || !startPending(key)) return;
    try {
      await onUpdate?.(productId, { is_active: !Boolean(product?.is_active), expected_updated_at: product?.updated_at });
    } catch { /* The parent owns failure feedback. */ } finally {
      finishPending(key);
    }
  }

  function openDeleteConfirmation(product) {
    const productId = product?.product_id;
    if (!canWrite || localPendingKeyRef.current || productId === null || productId === undefined || productId === "" || actionPending(deleteKey(productId))) return;
    cancelCreate();
    cancelEdit();
    setDeleteCandidate(product);
  }

  async function confirmDelete() {
    const productId = deleteCandidate?.product_id;
    const key = deleteKey(productId);
    if (!canWrite || productId === null || productId === undefined || productId === "" || !startPending(key)) return;
    try {
      if (await onDelete?.(productId, { expected_updated_at: deleteCandidate?.updated_at })) {
        setDeleteCandidate(null);
      }
    } catch { /* Keep the confirmation open after a rejected callback. */ } finally {
      finishPending(key);
    }
  }

  const isCreatingPending = actionPending("product-catalog:create");
  const hasLocalPending = Boolean(localPendingKey);
  const isFiltered = normalizeText(searchQuery).trim().length > 0;

  return (<>
    <section className={`${PANEL_CLASS_NAME} data-manager-viewport-panel`} aria-labelledby="data-manager-product-catalog-title">
      <div className="flex flex-wrap items-start justify-between gap-2.5">
        <div className="min-w-0">
          <h2 id="data-manager-product-catalog-title" className="text-[0.95rem] font-semibold tracking-[-0.02em] text-slate-950">Produits</h2>
          <p className="mt-1 text-[11px] leading-4 text-slate-500">Les produits inactifs restent sur les anciennes commandes.</p>
        </div>
        {canWrite && !isCreating ? (
          <button type="button" onClick={() => { cancelEdit(); setCreateName(""); setIsCreating(true); }} disabled={isCreatingPending || hasLocalPending} className={PRIMARY_ACTION_CLASS_NAME}>
            <Plus size={14} /> Ajouter un produit
          </button>
        ) : null}
      </div>

      <div className="data-manager-viewport-grid mt-3 overflow-auto rounded-[4px] border border-slate-200">
        <table className="w-full min-w-[34rem] border-collapse text-[11px] text-slate-700">
          <thead><tr>
            <th scope="col" className={`${HEADER_CELL_CLASS_NAME} w-full`}>Nom</th>
            <th scope="col" className={`${HEADER_CELL_CLASS_NAME} min-w-24`}>Statut</th>
            <th scope="col" className={`${HEADER_CELL_CLASS_NAME} min-w-72`}>Actions</th>
          </tr></thead>
          <tbody className="bg-white">
            {isCreating ? (
              <tr className="bg-slate-50/80"><td colSpan={3} className={CELL_CLASS_NAME}>
                <form className="flex flex-wrap items-center gap-1.5" onSubmit={submitCreate}>
                  <label className="sr-only" htmlFor="data-manager-product-create-name">Nom du produit</label>
                  <input id="data-manager-product-create-name" className={`${INPUT_CLASS_NAME} min-w-52 flex-1`} disabled={isCreatingPending || hasLocalPending} maxLength={MAX_PRODUCT_NAME_LENGTH} onChange={(event) => setCreateName(event.target.value)} placeholder="Nom du produit" required type="text" value={createName} />
                  <button type="submit" disabled={isCreatingPending || hasLocalPending || !createName.trim() || createName.length > MAX_PRODUCT_NAME_LENGTH} className={PRIMARY_ACTION_CLASS_NAME}><Save size={13} /> Sauvegarder</button>
                  <button type="button" onClick={cancelCreate} disabled={isCreatingPending || hasLocalPending} className={SECONDARY_ACTION_CLASS_NAME}>Annuler</button>
                </form>
              </td></tr>
            ) : null}

            {visibleProducts.map((product) => {
              const productId = product?.product_id;
              const pending = actionPending(updateKey(productId)) || actionPending(deleteKey(productId));
              const rowChangesDisabled = pending || hasLocalPending;
              const isEditing = editingProductId === productId;
              const isActive = Boolean(product?.is_active);
              return (
                <tr key={String(productId)} className={isEditing ? "bg-slate-50" : undefined}>
                  <td className={CELL_CLASS_NAME}>{isEditing ? (
                    <form onSubmit={(event) => submitEdit(event, product)}>
                      <label className="sr-only" htmlFor={`data-manager-product-edit-name-${productId}`}>Nom du produit</label>
                      <input id={`data-manager-product-edit-name-${productId}`} className={INPUT_CLASS_NAME} disabled={rowChangesDisabled} maxLength={MAX_PRODUCT_NAME_LENGTH} onChange={(event) => setEditingName(event.target.value)} required type="text" value={editingName} />
                    </form>
                  ) : <span className="block truncate text-[12px] font-medium text-slate-800">{productName(product) || "—"}</span>}</td>
                  <td className={CELL_CLASS_NAME}><span className={isActive ? "inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700" : "inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600"}>{isActive ? "Actif" : "Inactif"}</span></td>
                  <td className={`${CELL_CLASS_NAME} whitespace-nowrap`}><div className="flex flex-nowrap items-center gap-1.5">
                    {isEditing ? <>
                      <button type="button" onClick={(event) => submitEdit(event, product)} disabled={rowChangesDisabled || !editingName.trim() || editingName.length > MAX_PRODUCT_NAME_LENGTH} className={PRIMARY_ACTION_CLASS_NAME}><Save size={13} /> Sauvegarder</button>
                      <button type="button" onClick={cancelEdit} disabled={rowChangesDisabled} className={SECONDARY_ACTION_CLASS_NAME}>Annuler</button>
                    </> : <>
                      <span title={product?.rename_locked_reason || undefined} className="inline-flex">
                        <button type="button" onClick={() => startEdit(product)} disabled={!canWrite || rowChangesDisabled || Boolean(product?.rename_locked_reason)} className={SECONDARY_ACTION_CLASS_NAME}>Modifier</button>
                      </span>
                      <button type="button" onClick={() => toggleActive(product)} disabled={!canWrite || rowChangesDisabled} className={SECONDARY_ACTION_CLASS_NAME}>{isActive ? "Désactiver" : "Réactiver"}</button>
                      <button type="button" onClick={() => openDeleteConfirmation(product)} disabled={!canWrite || rowChangesDisabled} className={DANGER_ACTION_CLASS_NAME}><Trash2 size={13} /> Supprimer</button>
                    </>}
                  </div></td>
                </tr>
              );
            })}

            {!isCreating && visibleProducts.length === 0 ? <tr><td colSpan={3} className="px-3 py-6 text-center text-[11px] text-slate-500">{isFiltered ? "Aucun produit ne correspond à la recherche." : "Aucun produit n’est encore enregistré."}</td></tr> : null}
          </tbody>
        </table>
      </div>
    </section>
    {renameCandidate ? (
      <ConfirmationModal
        confirmLabel="Renommer partout"
        description="Le nouveau nom s’applique tout de suite, sans recalcul des tournées."
        eyebrow="Renommer un produit"
        impactItems={buildRenameImpactItems(productName(renameCandidate.product), renameCandidate.name, renameCandidate.preview)}
        isBusy={actionPending(updateKey(renameCandidate.product?.product_id))}
        onCancel={() => setRenameCandidate(null)}
        onClose={() => setRenameCandidate(null)}
        onConfirm={confirmRename}
        panelSize="compact"
        title={`Renommer « ${productName(renameCandidate.product)} » en « ${renameCandidate.name} » partout ?`}
        tone="accent"
      />
    ) : null}
    {deleteCandidate ? (
      <ConfirmationModal
        confirmLabel="Supprimer définitivement"
        description={`Le produit « ${productName(deleteCandidate)} » sera retiré du catalogue.`}
        eyebrow="Suppression d’un produit"
        impactItems={[
          "Ce produit ne pourra plus être sélectionné dans de nouvelles commandes ou correspondances.",
          "Les anciennes commandes, correspondances et sauvegardes conserveront leur libellé actuel.",
        ]}
        isBusy={actionPending(deleteKey(deleteCandidate?.product_id))}
        onCancel={() => setDeleteCandidate(null)}
        onClose={() => setDeleteCandidate(null)}
        onConfirm={confirmDelete}
        panelSize="compact"
        title={`Supprimer « ${productName(deleteCandidate)} » ?`}
        tone="danger"
        warningMessage="Cette suppression est définitive. Le produit devra être recréé pour redevenir disponible."
      />
    ) : null}
  </>);
}
